import React, { useMemo } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { Check, Close } from '@mui/icons-material';

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const requirements: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'Contains uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'Contains lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'Contains a number', test: (p) => /\d/.test(p) },
  { label: 'Contains special character', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

export const calculatePasswordStrength = (password: string): number => {
  if (!password) return 0;
  return requirements.filter((req) => req.test(password)).length;
};

export const isPasswordValid = (password: string): boolean => {
  return requirements.every((req) => req.test(password));
};

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ password }) => {
  const strength = useMemo(() => calculatePasswordStrength(password), [password]);
  const metRequirements = useMemo(
    () => requirements.map((req) => ({ ...req, met: req.test(password) })),
    [password]
  );

  const getStrengthLabel = (strength: number): string => {
    switch (strength) {
      case 0:
        return '';
      case 1:
        return 'Very Weak';
      case 2:
        return 'Weak';
      case 3:
        return 'Fair';
      case 4:
        return 'Good';
      case 5:
        return 'Strong';
      default:
        return '';
    }
  };

  const getStrengthColor = (strength: number): 'error' | 'warning' | 'info' | 'success' => {
    if (strength <= 1) return 'error';
    if (strength === 2) return 'warning';
    if (strength === 3 || strength === 4) return 'info';
    return 'success';
  };

  if (!password) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <LinearProgress
          variant="determinate"
          value={(strength / requirements.length) * 100}
          color={getStrengthColor(strength)}
          sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
        />
        <Typography
          variant="caption"
          sx={{ ml: 2, minWidth: 70, color: `${getStrengthColor(strength)}.main` }}
        >
          {getStrengthLabel(strength)}
        </Typography>
      </Box>
      <Box sx={{ pl: 1 }}>
        {metRequirements.map((req, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 0.5,
            }}
          >
            {req.met ? (
              <Check sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
            ) : (
              <Close sx={{ fontSize: 16, color: 'text.disabled', mr: 1 }} />
            )}
            <Typography
              variant="caption"
              sx={{
                color: req.met ? 'success.main' : 'text.secondary',
              }}
            >
              {req.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PasswordStrengthIndicator;
